import React, { useState, useEffect } from 'react';
import {
  Shield,
  Users,
  Calendar,
  RefreshCw,
  Download,
  Eye,
  AlertTriangle,
  Clock,
  Building2,
  User,
  ChevronDown,
  ChevronUp,
  FileText,
  CheckCircle,
  Settings,
  Save,
  Send,
  Bell,
  Mail,
  Webhook,
  Plus,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import toast from 'react-hot-toast';

interface PrivilegedUser {
  id: number;
  name: string;
  email: string;
  created_at: string;
  last_login: string | null;
  privileged_action_count: number;
  last_privileged_access: string | null;
  last_tenant_accessed: string | null;
  tenants_accessed_count: number;
}

interface TenantReport {
  tenant_id: number | null;
  tenant_name: string;
  total_actions: number;
  action_breakdown: {
    views: number;
    edits: number;
    deletes: number;
    creates: number;
    other: number;
  };
  first_access: string;
  last_access: string;
  admins: { user_id: number; user_name: string; action_count: number }[];
  categories: Record<string, number>;
}

interface TenantAccessEntry {
  id: number;
  admin_name: string;
  action: string;
  category: string;
  resource: string;
  timestamp: string;
  ip_address: string;
  status: string;
}

interface QuarterlyReviewUser {
  user_id: number;
  user_name: string;
  user_email: string;
  is_active: boolean;
  account_created: string;
  access_justification: string;
  quarterly_action_count: number;
  last_access_date: string | null;
  tenants_accessed: { id: number; name: string }[];
  review_status: string;
  review_decision: string | null;
  reviewer_notes: string | null;
}

interface AlertConfig {
  id: number;
  webhook_url: string;
  alert_email: string;
  alert_mode: 'immediate' | 'daily_digest' | 'weekly_digest' | 'disabled';
  digest_time: string;
  digest_day: number;
  alert_on_sensitive_only: boolean;
  sensitive_actions: string[];
  enabled: boolean;
  last_digest_sent: string | null;
}

const PrivilegedAccessReports: React.FC = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  const [activeTab, setActiveTab] = useState<'system-report' | 'tenant-log' | 'quarterly-review' | 'config'>('tenant-log');
  const [loading, setLoading] = useState(false);

  const [privilegedUsers, setPrivilegedUsers] = useState<PrivilegedUser[]>([]);
  const [tenantReports, setTenantReports] = useState<TenantReport[]>([]);
  const [tenantAccessLog, setTenantAccessLog] = useState<TenantAccessEntry[]>([]);
  const [quarterlyReview, setQuarterlyReview] = useState<QuarterlyReviewUser[]>([]);
  const [reviewPeriod, setReviewPeriod] = useState<{ quarter: string; year: number; start_date: string; end_date: string } | null>(null);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  const [summary, setSummary] = useState<{ total_privileged_actions: number; tenants_accessed: number; unique_admins: number } | null>(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedTenant, setExpandedTenant] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Config editing state
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState<AlertConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingAlert, setTestingAlert] = useState<'webhook' | 'email' | null>(null);
  const [newSensitiveAction, setNewSensitiveAction] = useState('');

  useEffect(() => {
    if (activeTab === 'tenant-log') {
      fetchTenantAccessLog();
    }
  }, [activeTab, page, startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'system-report' && isSuperAdmin) {
      fetchSystemReport();
      fetchPrivilegedUsers();
    }
  }, [activeTab, isSuperAdmin, startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'quarterly-review' && isSuperAdmin) {
      fetchQuarterlyReview();
    }
  }, [activeTab, isSuperAdmin]);

  useEffect(() => {
    if (activeTab === 'config' && isSuperAdmin) {
      fetchAlertConfig();
    }
  }, [activeTab, isSuperAdmin]);

  const fetchTenantAccessLog = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      });

      const response = await api.get(`/api/admin/privileged-access/tenant-access-log?${params}`);

      if (response.data.success) {
        setTenantAccessLog(response.data.entries);
        setTotalPages(response.data.pagination?.pages || 1);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setTenantAccessLog([]);
      } else {
        console.error('Error fetching tenant access log:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      });

      const response = await api.get(`/api/admin/privileged-access/system/privileged-access-report?${params}`);

      if (response.data.success) {
        setTenantReports(response.data.report);
        setSummary(response.data.summary);
      }
    } catch (error: any) {
      console.error('Error fetching system report:', error);
      toast.error('Failed to fetch privileged access report');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrivilegedUsers = async () => {
    try {
      const response = await api.get('/api/admin/privileged-access/system/privileged-users');

      if (response.data.success) {
        setPrivilegedUsers(response.data.privileged_users);
      }
    } catch (error: any) {
      console.error('Error fetching privileged users:', error);
    }
  };

  const fetchQuarterlyReview = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/privileged-access/system/quarterly-review');

      if (response.data.success) {
        setQuarterlyReview(response.data.privileged_users);
        setReviewPeriod(response.data.review_period);
      }
    } catch (error: any) {
      console.error('Error fetching quarterly review:', error);
      toast.error('Failed to fetch quarterly review data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertConfig = async () => {
    try {
      const response = await api.get('/api/admin/privileged-access/system/alert-config');

      if (response.data.success) {
        setAlertConfig(response.data.config);
        setConfigForm(response.data.config);
      }
    } catch (error: any) {
      console.error('Error fetching alert config:', error);
    }
  };

  const saveAlertConfig = async () => {
    if (!configForm) return;

    try {
      setSavingConfig(true);
      const response = await api.put('/api/admin/privileged-access/system/alert-config', configForm);

      if (response.data.success) {
        setAlertConfig(response.data.config);
        setConfigForm(response.data.config);
        setEditingConfig(false);
        toast.success('Alert configuration saved successfully');
      }
    } catch (error: any) {
      console.error('Error saving alert config:', error);
      toast.error('Failed to save alert configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const testAlert = async (type: 'webhook' | 'email') => {
    try {
      setTestingAlert(type);
      const response = await api.post('/api/admin/privileged-access/system/test-alert', { type });

      if (response.data.success && response.data.result.success) {
        toast.success(`Test ${type} alert sent successfully`);
      } else {
        toast.error(`Test ${type} failed: ${response.data.result?.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error sending test alert:', error);
      toast.error(`Failed to send test ${type} alert`);
    } finally {
      setTestingAlert(null);
    }
  };

  const addSensitiveAction = () => {
    if (!configForm || !newSensitiveAction.trim()) return;
    if (configForm.sensitive_actions.includes(newSensitiveAction.toLowerCase().trim())) {
      toast.error('This action keyword already exists');
      return;
    }
    setConfigForm({
      ...configForm,
      sensitive_actions: [...configForm.sensitive_actions, newSensitiveAction.toLowerCase().trim()]
    });
    setNewSensitiveAction('');
  };

  const removeSensitiveAction = (action: string) => {
    if (!configForm) return;
    setConfigForm({
      ...configForm,
      sensitive_actions: configForm.sensitive_actions.filter(a => a !== action)
    });
  };

  const exportReport = () => {
    const headers = ['Tenant', 'Total Actions', 'Views', 'Edits', 'Deletes', 'Creates', 'First Access', 'Last Access'];
    const rows = tenantReports.map(r => [
      r.tenant_name,
      r.total_actions,
      r.action_breakdown.views,
      r.action_breakdown.edits,
      r.action_breakdown.deletes,
      r.action_breakdown.creates,
      new Date(r.first_access).toLocaleString(),
      new Date(r.last_access).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `privileged-access-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Privileged Access Monitoring</h2>
              <p className="text-sm text-gray-500">Track and review Super Admin access to tenant data</p>
            </div>
          </div>
        </div>

        <div className="flex space-x-2 border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('tenant-log')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'tenant-log'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Eye className="w-4 h-4" />
              <span>Tenant Access Log</span>
            </div>
          </button>

          {isSuperAdmin && (
            <>
              <button
                onClick={() => setActiveTab('system-report')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'system-report'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>System Report</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('quarterly-review')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'quarterly-review'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>Quarterly Review</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('config')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'config'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>Alert Configuration</span>
                </div>
              </button>
            </>
          )}
        </div>

        {activeTab === 'tenant-log' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <button
                  onClick={fetchTenantAccessLog}
                  className="mt-5 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 flex items-center space-x-1"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            {tenantAccessLog.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Privileged Access Records</h3>
                <p className="text-gray-500">No Super Admin access to your tenant data has been recorded.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tenantAccessLog.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-red-600" />
                            </div>
                            <span className="font-medium text-gray-900">{entry.admin_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{entry.action}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{entry.resource || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(entry.timestamp)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{entry.ip_address}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center space-x-2 mt-4">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'system-report' && isSuperAdmin && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <button
                  onClick={fetchSystemReport}
                  className="mt-5 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 flex items-center space-x-1"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
              <button
                onClick={exportReport}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>

            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600">Total Privileged Actions</p>
                      <p className="text-2xl font-bold text-red-900">{summary.total_privileged_actions}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Tenants Accessed</p>
                      <p className="text-2xl font-bold text-blue-900">{summary.tenants_accessed}</p>
                    </div>
                    <Building2 className="w-8 h-8 text-blue-400" />
                  </div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600">Unique Admins</p>
                      <p className="text-2xl font-bold text-purple-900">{summary.unique_admins}</p>
                    </div>
                    <Users className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600">Privileged Users</p>
                      <p className="text-2xl font-bold text-green-900">{privilegedUsers.length}</p>
                    </div>
                    <Shield className="w-8 h-8 text-green-400" />
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Access by Tenant</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {tenantReports.map((report, index) => (
                  <div key={index} className="p-4">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedTenant(expandedTenant === index ? null : index)}
                    >
                      <div className="flex items-center space-x-3">
                        <Building2 className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{report.tenant_name}</p>
                          <p className="text-sm text-gray-500">{report.total_actions} actions</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Last Access</p>
                          <p className="text-sm font-medium">{formatDate(report.last_access)}</p>
                        </div>
                        {expandedTenant === index ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {expandedTenant === index && (
                      <div className="mt-4 pl-8 space-y-4">
                        <div className="grid grid-cols-5 gap-4">
                          <div className="bg-gray-50 rounded p-3 text-center">
                            <p className="text-lg font-bold text-gray-900">{report.action_breakdown.views}</p>
                            <p className="text-xs text-gray-500">Views</p>
                          </div>
                          <div className="bg-gray-50 rounded p-3 text-center">
                            <p className="text-lg font-bold text-gray-900">{report.action_breakdown.creates}</p>
                            <p className="text-xs text-gray-500">Creates</p>
                          </div>
                          <div className="bg-gray-50 rounded p-3 text-center">
                            <p className="text-lg font-bold text-gray-900">{report.action_breakdown.edits}</p>
                            <p className="text-xs text-gray-500">Edits</p>
                          </div>
                          <div className="bg-gray-50 rounded p-3 text-center">
                            <p className="text-lg font-bold text-red-600">{report.action_breakdown.deletes}</p>
                            <p className="text-xs text-gray-500">Deletes</p>
                          </div>
                          <div className="bg-gray-50 rounded p-3 text-center">
                            <p className="text-lg font-bold text-gray-900">{report.action_breakdown.other}</p>
                            <p className="text-xs text-gray-500">Other</p>
                          </div>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Admins who accessed:</p>
                          <div className="flex flex-wrap gap-2">
                            {report.admins.map((admin, i) => (
                              <span key={i} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                                {admin.user_name} ({admin.action_count})
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {tenantReports.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    No privileged access recorded in this period
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'quarterly-review' && isSuperAdmin && (
          <div className="space-y-6">
            {reviewPeriod && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-900">
                      {reviewPeriod.quarter} {reviewPeriod.year} Review Period
                    </h3>
                    <p className="text-sm text-blue-700">
                      {new Date(reviewPeriod.start_date).toLocaleDateString()} - {new Date(reviewPeriod.end_date).toLocaleDateString()}
                    </p>
                  </div>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Export Review</span>
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Justification</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions (Quarter)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Access</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenants</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {quarterlyReview.map((user) => (
                    <tr key={user.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{user.user_name}</p>
                          <p className="text-sm text-gray-500">{user.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.access_justification}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-gray-900">{user.quarterly_action_count}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {user.last_access_date ? formatDate(user.last_access_date) : 'None'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{user.tenants_accessed.length} tenant(s)</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.review_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {user.review_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2">Review Instructions</h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>1. Review each privileged user's access during this quarter</li>
                <li>2. Verify access justification is still valid</li>
                <li>3. Check if any users should have access revoked</li>
                <li>4. Document review decision and any actions taken</li>
                <li>5. Export and sign off on the review</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'config' && isSuperAdmin && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-900">Alert Configuration</h3>
                {!editingConfig ? (
                  <button
                    onClick={() => setEditingConfig(true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Edit Configuration</span>
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setEditingConfig(false);
                        setConfigForm(alertConfig);
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveAlertConfig}
                      disabled={savingConfig}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      <span>{savingConfig ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                  </div>
                )}
              </div>

              {configForm && (
                <div className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Bell className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="font-medium">Enable Alerts</span>
                        <p className="text-sm text-gray-500">Turn privileged access alerts on or off</p>
                      </div>
                    </div>
                    {editingConfig ? (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={configForm.enabled}
                          onChange={(e) => setConfigForm({ ...configForm, enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${configForm.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {configForm.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>

                  {/* Webhook URL */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <Webhook className="w-5 h-5 text-gray-400" />
                      <div className="flex-1">
                        <span className="font-medium">Webhook URL</span>
                        <p className="text-sm text-gray-500">Send alerts to Slack, Teams, or SIEM systems</p>
                      </div>
                    </div>
                    {editingConfig ? (
                      <input
                        type="url"
                        value={configForm.webhook_url}
                        onChange={(e) => setConfigForm({ ...configForm, webhook_url: e.target.value })}
                        placeholder="https://hooks.slack.com/services/..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${configForm.webhook_url ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className="text-sm text-gray-600">
                            {configForm.webhook_url || 'Not configured'}
                          </span>
                        </div>
                        {configForm.webhook_url && (
                          <button
                            onClick={() => testAlert('webhook')}
                            disabled={testingAlert === 'webhook'}
                            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center space-x-2 text-sm disabled:opacity-50"
                          >
                            <Send className="w-4 h-4" />
                            <span>{testingAlert === 'webhook' ? 'Sending...' : 'Send Test Webhook'}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Alert Email */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div className="flex-1">
                        <span className="font-medium">Alert Email</span>
                        <p className="text-sm text-gray-500">Email address for alert notifications</p>
                      </div>
                    </div>
                    {editingConfig ? (
                      <input
                        type="email"
                        value={configForm.alert_email}
                        onChange={(e) => setConfigForm({ ...configForm, alert_email: e.target.value })}
                        placeholder="security@example.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${configForm.alert_email ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className="text-sm text-gray-600">
                            {configForm.alert_email || 'Not configured'}
                          </span>
                        </div>
                        {configForm.alert_email && (
                          <button
                            onClick={() => testAlert('email')}
                            disabled={testingAlert === 'email'}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2 text-sm disabled:opacity-50"
                          >
                            <Send className="w-4 h-4" />
                            <span>{testingAlert === 'email' ? 'Sending...' : 'Send Test Email'}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Alert Mode */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="font-medium">Alert Mode</span>
                        <p className="text-sm text-gray-500">When to send alert notifications</p>
                      </div>
                    </div>
                    {editingConfig ? (
                      <select
                        value={configForm.alert_mode}
                        onChange={(e) => setConfigForm({ ...configForm, alert_mode: e.target.value as AlertConfig['alert_mode'] })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="immediate">Immediate - Send alert for each sensitive action</option>
                        <option value="daily_digest">Daily Digest - Summary once per day</option>
                        <option value="weekly_digest">Weekly Digest - Summary once per week</option>
                        <option value="disabled">Disabled - No alerts</option>
                      </select>
                    ) : (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium capitalize">
                        {configForm.alert_mode.replace('_', ' ')}
                      </span>
                    )}
                  </div>

                  {/* Digest Time (shown when digest mode is selected) */}
                  {(configForm.alert_mode === 'daily_digest' || configForm.alert_mode === 'weekly_digest') && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3 mb-3">
                        <Calendar className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="font-medium">Digest Schedule</span>
                          <p className="text-sm text-gray-500">When to send the digest email</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        {editingConfig ? (
                          <>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Time</label>
                              <input
                                type="time"
                                value={configForm.digest_time}
                                onChange={(e) => setConfigForm({ ...configForm, digest_time: e.target.value })}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            {configForm.alert_mode === 'weekly_digest' && (
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Day</label>
                                <select
                                  value={configForm.digest_day}
                                  onChange={(e) => setConfigForm({ ...configForm, digest_day: parseInt(e.target.value) })}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value={0}>Sunday</option>
                                  <option value={1}>Monday</option>
                                  <option value={2}>Tuesday</option>
                                  <option value={3}>Wednesday</option>
                                  <option value={4}>Thursday</option>
                                  <option value={5}>Friday</option>
                                  <option value={6}>Saturday</option>
                                </select>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-600">
                            {configForm.alert_mode === 'weekly_digest'
                              ? `Every ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][configForm.digest_day]} at ${configForm.digest_time}`
                              : `Daily at ${configForm.digest_time}`
                            }
                          </span>
                        )}
                      </div>
                      {configForm.last_digest_sent && (
                        <p className="text-xs text-gray-500 mt-2">
                          Last digest sent: {formatDate(configForm.last_digest_sent)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Alert on Sensitive Only */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <AlertTriangle className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="font-medium">Alert on Sensitive Actions Only</span>
                          <p className="text-sm text-gray-500">Only alert when actions contain sensitive keywords</p>
                        </div>
                      </div>
                      {editingConfig ? (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={configForm.alert_on_sensitive_only}
                            onChange={(e) => setConfigForm({ ...configForm, alert_on_sensitive_only: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${configForm.alert_on_sensitive_only ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                          {configForm.alert_on_sensitive_only ? 'Yes' : 'No (All Actions)'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Sensitive Actions Keywords */}
                  {configForm.alert_on_sensitive_only && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3 mb-3">
                        <Shield className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="font-medium">Sensitive Action Keywords</span>
                          <p className="text-sm text-gray-500">Actions containing these words will trigger alerts</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {configForm.sensitive_actions.map((action, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm flex items-center space-x-1"
                          >
                            <span>{action}</span>
                            {editingConfig && (
                              <button
                                onClick={() => removeSensitiveAction(action)}
                                className="ml-1 hover:text-red-900"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {editingConfig && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newSensitiveAction}
                            onChange={(e) => setNewSensitiveAction(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addSensitiveAction()}
                            placeholder="Add keyword..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                          <button
                            onClick={addSensitiveAction}
                            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Info box */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">How Alerts Work</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>Immediate mode:</strong> Sends an alert instantly for each sensitive action</li>
                  <li>• <strong>Daily digest:</strong> Collects all actions and sends one summary email/webhook per day</li>
                  <li>• <strong>Weekly digest:</strong> Collects all actions and sends one summary per week</li>
                  <li>• <strong>Sensitive only:</strong> Only alerts when action types contain keywords like "delete", "password", "role"</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivilegedAccessReports;
